import React, { useState, useCallback } from 'react';
import { toast } from 'react-toastify';
import { Link } from 'react-router-dom';
import { useApi } from '../../hooks/useApi';
import { useAuthUserContext } from '../../providers/AuthUser';
import { Conv } from '../../models/Conv';
import { Message, ApiMessage } from '../../models/Message';
import { UserType } from '../../types';
import MessageAside from './MessageAside';
import MessageForm from './MessageForm';
import Spinner from '../../components/Spinner';

const MyMessage = () => {
  const [ isNoConv, setIsNoConv ] = useState<boolean>(false);
  const [ activeConv, setActiveConv ] = useState<Conv>();
  const [ messages, setMessages ] = useState<Message[]>();
  const [ refreshTrigger, setRefreshTrigger ] = useState<number>(0);
  const { get, post, loading } = useApi();
  const { user } = useAuthUserContext();

  const isDonee = user?.userType === UserType.Donee;

  const selectConv = useCallback((conv?: Conv) => {
    if (!conv) return;

    setActiveConv(conv);

    const changeMessagesToRead = (convId: number) => {
      get({
        endpoint: `/api/v1/convs/${ convId }/update_to_read`,
        onError: (err) => {
          console.error(err);
        },
      });
    }

    get({
      endpoint: `/api/v1/convs/${ conv.id }/messages`,
      onSuccess: (res) => {
        const apiMessages: ApiMessage[] = res.data;
        setMessages(apiMessages.map(m => new Message(m)));
        changeMessagesToRead(conv.id);
      },
      onError: (err) => {
        toast.error("メッセージの取得に失敗しました。時間をおいて再度お試しください。");
      },
    });
  },[ get ]);

  const handleHideConv = (convId: number, e: React.MouseEvent) => {
    e.stopPropagation();
    post({
      endpoint: `/api/v1/convs/${ convId }/hide`,
      method: 'PUT',
      onSuccess: () => {
        toast.success('メッセージを非表示にしました');
        setRefreshTrigger(prev => prev + 1);
      },
      onError: () => {
        toast.error('非表示に失敗しました');
      },
    });
  };

  const handleUnhideConv = (convId: number, e: React.MouseEvent) => {
    e.stopPropagation();
    post({
      endpoint: `/api/v1/convs/${ convId }/unhide`,
      method: 'PUT',
      onSuccess: () => {
        toast.success('メッセージを再表示しました');
        setRefreshTrigger(prev => prev + 1);
      },
      onError: () => {
        toast.error('再表示に失敗しました');
      },
    });
  };


  const messageList = messages?.map(m => {
    return (
      <li key={ m.id }>
        <h4 className={ m.sender.userType === UserType.Donee ? "txt_pink" : "txt_blue"}>
          { m.sender.name }&nbsp;&nbsp;{ m.sentAtJst }
        </h4>
        <p>
          { m.body }
        </p>
      </li>
    )
  })

  const mainContents = () => {
    if (isNoConv) {
      if (isDonee) {
        return (
          <p className="no_message">
            ※まだメッセージはございません。<br/><Link to="/search-result">「ドナーを探す」</Link>よりご希望にあったドナーを検索し、ドナープロフィール画面下部からメッセージを送ることができます。
          </p>
        )
      } else {
        return (
          <p className="no_message">
            ※まだドニーからのメッセージはございません。<br/>プロフィールの詳細を記入後、公開することで「ドナーを探す」に掲載されるようになります。<br/>また、検査結果の登録をすることでドニーに見つけてもらいやすくなりますので、まだ未登録の方は登録をおすすめいたします。
          </p>
        )
      }
    } else if (!activeConv) {
      return (
        <p>メッセージを選択してください。</p>
      )
    } else {
      return (
        <>
          <MessageForm
            activeConv={ activeConv }
            selectConv={ selectConv }
          />
          <h3 className="sub_title">
            { isDonee ? activeConv?.donor.name : activeConv?.donee.name }（No.{ isDonee ? activeConv?.donor.userNo : activeConv?.donee.userNo }）さんとのメッセージ履歴
          </h3>
          <ul className="btnarea">
            <li>
              { activeConv.hidden ? (
                <button onClick={ (e) => handleUnhideConv(activeConv.id, e) } className={ isDonee ? "w_pinkbtn" : "w_bluebtn" }>
                  この会話を再表示する
                </button>
              ) : (
                <button onClick={ (e) => handleHideConv(activeConv.id, e) } className={ isDonee ? "w_pinkbtn" : "w_bluebtn" }>
                  この会話を非表示にする
                </button>
              ) }
            </li>
          </ul>
          <ul className="messge_list">
            { loading ? <Spinner centered={ true } color={ isDonee ? "pink" : "blue" }/> : messageList }
          </ul>
        </>
      )
    }
  }

  return (
    <section className={ isDonee ? "donee_message pink" : "donor_message" }>
      <h2 className={ isDonee ? "main_title pink" : "main_title" }><span>メッセージ</span></h2>
      <div className="wrap">
        <MessageAside
          activeConv={ activeConv }
          selectConv={ selectConv }
          setIsNoConv={ setIsNoConv }
          refreshTrigger={ refreshTrigger }
        />
        <div className="mainwrap">
          { loading ? <Spinner centered={ true } color={ isDonee ? "pink" : "blue" } /> : mainContents() }
        </div>
      </div>
    </section>
  );
}

export default MyMessage;
